import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Modal,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  IconButton,
  Divider,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Avatar,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import LoginIcon from '@mui/icons-material/Login';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useUser } from '../context/UserContext';
import { getUsersList, user_new, loginAsUser, deleteUser, modifyUser } from '../services/api';
import { HASH } from '../utils/helpers';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  boxShadow: 24,
  p: 4,
};

// Estilos personalizados para las celdas de la tabla
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: '16px',
  '&.header': {
    backgroundColor: theme.palette.background.paper,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    height: '57px',
  }
}));

const Usuarios = () => {
  const theme = useTheme();
  const { user } = useUser();
  const isAdmin = user?.rol === 'admin';
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    organization: '',
    name: '',
    email: '',
    rol: 'user'
  });
  const [errors, setErrors] = useState({
    email: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const [deleteTimer, setDeleteTimer] = useState(null);
  const HOLD_TIME = 1000; // 1 segundos
  const [openModifyModal, setOpenModifyModal] = useState(false);
  const [userToModify, setUserToModify] = useState(null);
  const [modifiedUserData, setModifiedUserData] = useState({
    name: '',
    email: '',
    organization: '',
    rol: ''
  });

  // Función para formatear el rol
  const formatRol = (rol) => {
    switch (rol) {
      case 'admin':
        return 'Admin';
      case 'partner':
        return 'Partner';
      case 'user':
        return 'Usuario';
      case 'readonly':
        return 'Solo lectura';
      default:
        return rol;
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getUsersList();
        if (response?.users) {
          // Enriquecer los datos de usuarios con los nombres de las organizaciones
          const enrichedUsers = response.users.map(u => {
            const org = user?.organizations?.find(org => org.code === u.organization);
            return {
              ...u,
              organizationName: org ? org.name : u.organization
            };
          });
          setUsers(enrichedUsers);
        }
      } catch (error) {
        console.error('Error al obtener la lista de usuarios:', error);
      }
    };

    fetchUsers();
  }, [user?.organizations]);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    setNewUser({ organization: '', name: '', email: '', rol: 'user' });
    setErrors({ email: '' });
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({
      ...prev,
      [name]: value
    }));

    // Validación de email en tiempo real
    if (name === 'email') {
      setErrors(prev => ({
        ...prev,
        email: validateEmail(value) ? '' : 'Email inválido'
      }));
    }
  };

  // Función para generar una contraseña aleatoria (2 letras + 4 números)
  const generatePassword = () => {
    const letters = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';

    let password = '';
    // Generar 2 letras aleatorias
    for (let i = 0; i < 2; i++) {
      password += letters.charAt(Math.floor(Math.random() * letters.length));
    }
    // Generar 4 números aleatorios
    for (let i = 0; i < 4; i++) {
      password += numbers.charAt(Math.floor(Math.random() * numbers.length));
    }
    return password;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar antes de enviar
    if (!validateEmail(newUser.email)) {
      setErrors({
        ...errors,
        email: 'El formato del email no es válido'
      });
      return;
    }

    try {
      // Generar contraseña visible
      const passvisible = generatePassword();
      // Generar hash de la contraseña usando la función HASH
      const passhash = HASH(passvisible);

      // Llamar a la API para crear el usuario
      const response = await user_new(
        newUser.organization,
        newUser.email,
        newUser.name,
        newUser.rol,
        passhash,
        passvisible
      );

      if (response) {
        // Actualizar la lista de usuarios
        const updatedUsers = await getUsersList();
        if (updatedUsers?.users) {
          // Enriquecer los datos de usuarios con los nombres de las organizaciones
          const enrichedUsers = updatedUsers.users.map(u => {
            const org = user?.organizations?.find(org => org.code === u.organization);
            return {
              ...u,
              organizationName: org ? org.name : u.organization
            };
          });
          setUsers(enrichedUsers);
        }

        // Cerrar el modal y limpiar el formulario
        handleCloseModal();

        // Mostrar mensaje de éxito con Snackbar
        setSnackbar({
          open: true,
          message: `Usuario creado exitosamente.\nEnviado correo a ${newUser.email} con sus credenciales de acceso.`,
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error al crear el usuario:', error);
      // Mostrar el error específico si el correo ya existe
      if (error.message === 'El correo electrónico ya existe') {
        setErrors({
          ...errors,
          email: 'Este correo electrónico ya está registrado'
        });
      } else {
        // Mostrar error genérico con Snackbar
        setSnackbar({
          open: true,
          message: 'Error al crear el usuario. Por favor, inténtalo de nuevo.',
          severity: 'error'
        });
      }
    }
  };

  // Función para manejar el login como usuario
  const handleLoginAsUser = async (userEmail) => {
    try {
      const loginData = await loginAsUser(userEmail);

      // Abrir nueva ventana
      const newWindow = window.open('/', '_blank');

      // Cuando la ventana esté lista, configurar la sesión
      newWindow.onload = () => {
        // Guardar los datos de la sesión
        newWindow.sessionStorage.setItem('sessionId', loginData.session);
        newWindow.sessionStorage.setItem('user', JSON.stringify({
          ...loginData,
          photo: loginData.photo ? `/photos/${loginData.photo}` : null, // Ajustar la ruta de la foto
          impersonated: true // Marcar que es una sesión impersonada
        }));

        // Recargar para inicializar con la nueva sesión
        newWindow.location.reload();

        // Manejar el cierre de la ventana
        newWindow.onbeforeunload = () => {
          // Limpiar la sesión al cerrar
          newWindow.sessionStorage.removeItem('sessionId');
          newWindow.sessionStorage.removeItem('user');
        };
      };
    } catch (error) {
      console.error('Error al hacer login como usuario:', error);
      // Mostrar el error en el snackbar
      setSnackbar({
        open: true,
        message: 'Error al acceder como usuario: ' + error.message,
        severity: 'error'
      });
    }
  };

  // Función para abrir el modal de eliminación
  const handleOpenDeleteModal = (user) => {
    setUserToDelete(user);
    setOpenDeleteModal(true);
  };

  // Función para cerrar el modal de eliminación
  const handleCloseDeleteModal = () => {
    stopDeleteTimer();
    setUserToDelete(null);
    setOpenDeleteModal(false);
  };

  // Función para confirmar la eliminación
  const handleConfirmDelete = async () => {
    try {
      setIsLoading(true);
      await deleteUser(userToDelete.email);

      // Obtener la lista actualizada de usuarios
      const response = await getUsersList();
      if (response?.users) {
        // Enriquecer los datos de usuarios con los nombres de las organizaciones
        const enrichedUsers = response.users.map(u => {
          const org = user?.organizations?.find(org => org.code === u.organization);
          return {
            ...u,
            organizationName: org ? org.name : u.organization
          };
        });
        setUsers(enrichedUsers);
      }

      handleCloseDeleteModal();
      setSnackbar({
        open: true,
        message: `Usuario ${userToDelete.email} eliminado correctamente`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Función para iniciar el timer de eliminación
  const startDeleteTimer = () => {
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      const progress = (elapsedTime / HOLD_TIME) * 100;

      if (progress >= 100) {
        clearInterval(timer);
        handleConfirmDelete();
      } else {
        setDeleteProgress(progress);
      }
    }, 10);
    setDeleteTimer(timer);
  };

  // Función para detener el timer de eliminación
  const stopDeleteTimer = () => {
    if (deleteTimer) {
      clearInterval(deleteTimer);
      setDeleteTimer(null);
    }
    setDeleteProgress(0);
  };

  // Función para abrir el modal de modificación
  const handleOpenModifyModal = (user) => {
    setUserToModify(user);
    setModifiedUserData({
      name: user.name,
      email: user.email,
      organization: user.organization,
      rol: user.rol
    });
    setOpenModifyModal(true);
  };

  // Función para cerrar el modal de modificación
  const handleCloseModifyModal = () => {
    setUserToModify(null);
    setModifiedUserData({
      name: '',
      email: '',
      organization: '',
      rol: ''
    });
    setOpenModifyModal(false);
  };

  // Función para manejar cambios en los campos
  const handleModifyInputChange = (field) => (event) => {
    setModifiedUserData({
      ...modifiedUserData,
      [field]: event.target.value
    });
  };

  // Función para confirmar la modificación
  const handleConfirmModify = async () => {
    try {
      setIsLoading(true);
      const currentEmail = userToModify.email;

      // Si el email nuevo es igual al actual, enviamos el actual
      const emailToSend = modifiedUserData.email === currentEmail ? currentEmail : modifiedUserData.email;

      await modifyUser(currentEmail, {
        ...userToModify, // Mantenemos todos los valores actuales
        ...modifiedUserData, // Sobreescribimos solo los que se han modificado
        email: emailToSend // Aseguramos que el email se maneje correctamente
      });

      // Obtener la lista actualizada de usuarios
      const response = await getUsersList();
      if (response?.users) {
        // Enriquecer los datos de usuarios con los nombres de las organizaciones
        const enrichedUsers = response.users.map(u => {
          const org = user?.organizations?.find(org => org.code === u.organization);
          return {
            ...u,
            organizationName: org ? org.name : u.organization
          };
        });
        setUsers(enrichedUsers);
      }

      handleCloseModifyModal();
      setSnackbar({
        open: true,
        message: 'Usuario modificado correctamente',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth={false}>
      <Box sx={{ p: 0 }}>
        {/* Header */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2.5, sm: 3 },
            mb: { xs: 2.5, sm: 3.5 },
            borderRadius: 1
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box>
              <Typography variant="h3" component="h1" sx={{ color: theme.palette.text.primary, mb: 0.5, fontWeight: 800, letterSpacing: '-0.03em', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                Usuarios
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '1rem', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                Gestiona tus usuarios. Dales de alta o consulta sus datos.
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', mt: 1 }}>
                Total usuarios: {users.length}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ color: theme.palette.primary.contrastText }} />}
              onClick={handleOpenModal}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  borderColor: theme.palette.primary.dark,
                },
                height: '40px',
                textTransform: 'none',
                fontSize: '0.9rem',
                fontWeight: 500,
                width: { xs: '100%', sm: 'auto' },
                flex: { xs: '1 1 auto', sm: '0 0 auto' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '& .MuiButton-startIcon': {
                  marginRight: 1,
                  '& svg': {
                    fontSize: '1.2rem'
                  }
                }
              }}
            >
              Nuevo Usuario
            </Button>
          </Box>
        </Paper>

        {/* Table Box */}
        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            mt: 2
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell className="header" sx={{ width: '40px', padding: '8px' }}></StyledTableCell>
                  <StyledTableCell className="header">Email</StyledTableCell>
                  <StyledTableCell className="header">Nombre</StyledTableCell>
                  <StyledTableCell className="header">Organización</StyledTableCell>
                  <StyledTableCell className="header">Rol</StyledTableCell>
                  {/* Solo mostrar las columnas de acciones si el usuario es admin */}
                  {isAdmin && (
                    <>
                      <StyledTableCell className="header" sx={{ width: '100px', padding: '6px' }}>Acciones</StyledTableCell>
                      <StyledTableCell className="header" align="center" sx={{ width: '50px', padding: '6px' }}>Login</StyledTableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user, index) => (
                  <TableRow
                    key={user.email}
                    sx={{
                      backgroundColor: index % 2 === 0 ? 'transparent' : theme.palette.action.hover,
                      '&:hover': {
                        backgroundColor: theme.palette.action.selected,
                      },
                      cursor: 'pointer',
                    }}
                  >
                    <StyledTableCell sx={{ width: '40px', padding: '4px 8px' }}>
                      <Avatar
                        src={user.photo ? `https://overlord.chat/photos/${user.photo}` : ''}
                        alt={user.name}
                        sx={{
                          width: 32,
                          height: 32
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>{user.email}</StyledTableCell>
                    <StyledTableCell>{user.name}</StyledTableCell>
                    <StyledTableCell>{user.organizationName}</StyledTableCell>
                    <StyledTableCell>{formatRol(user.rol)}</StyledTableCell>
                    {isAdmin && (
                      <>
                        <StyledTableCell sx={{ width: '100px', padding: '6px' }}>
                          <Stack direction="row" spacing={0.5} justifyContent="flex-start">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenModifyModal(user)}
                              sx={{
                                backgroundColor: theme.palette.action.hover,
                                color: theme.palette.primary.main,
                                border: `1px solid ${theme.palette.divider}`,
                                '&:hover': {
                                  backgroundColor: theme.palette.action.selected,
                                  borderColor: theme.palette.primary.main,
                                },
                                width: '32px',
                                height: '32px'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteModal(user)}
                              sx={{
                                backgroundColor: theme.palette.action.hover,
                                color: theme.palette.error.main,
                                border: `1px solid ${theme.palette.error.main}`,
                                '&:hover': {
                                  backgroundColor: theme.palette.action.selected,
                                  borderColor: theme.palette.error.dark,
                                },
                                width: '32px',
                                height: '32px'
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </StyledTableCell>
                        <StyledTableCell align="center" sx={{ width: '50px', padding: '16px' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleLoginAsUser(user.email)}
                            sx={{
                              backgroundColor: theme.palette.action.hover,
                              color: theme.palette.success.main,
                              border: `1px solid ${theme.palette.success.main}`,
                              '&:hover': {
                                backgroundColor: theme.palette.action.selected,
                                borderColor: theme.palette.success.dark,
                              },
                              width: '32px',
                              height: '32px'
                            }}
                          >
                            <LoginIcon fontSize="small" />
                          </IconButton>
                        </StyledTableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Modal de nuevo usuario */}
        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="modal-nuevo-usuario"
          aria-describedby="modal-crear-nuevo-usuario"
        >
          <Paper sx={{ ...modalStyle, bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
            {/* Header con título centrado */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              mb: 2
            }}>
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.primary,
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  textAlign: 'center'
                }}
              >
                Nuevo Usuario
              </Typography>
            </Box>

            <Divider sx={{
              mb: 3,
              backgroundColor: theme.palette.divider,
              opacity: 0.5
            }} />

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: theme.palette.text.secondary }}>Organización</InputLabel>
                  <Select
                    name="organization"
                    value={newUser.organization}
                    onChange={handleInputChange}
                    required
                    sx={{
                      color: theme.palette.text.primary,
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.divider
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.light
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: theme.palette.background.paper,
                          '& .MuiMenuItem-root': {
                            color: theme.palette.text.primary
                          },
                          '& .MuiMenuItem-root:hover': {
                            bgcolor: theme.palette.action.hover
                          }
                        }
                      }
                    }}
                  >
                    {user?.organizations?.map((org) => (
                      <MenuItem
                        key={org.code}
                        value={org.code}
                      >
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Nombre"
                  name="name"
                  value={newUser.name}
                  onChange={handleInputChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.light,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                    '& .MuiOutlinedInput-input': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={newUser.email}
                  onChange={handleInputChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.light,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                    '& .MuiOutlinedInput-input': {
                      color: theme.palette.text.primary,
                    },
                    '& .MuiFormHelperText-root': {
                      color: theme.palette.error.main,
                    },
                  }}
                />
                {(user?.rol === 'admin' || user?.rol === 'partner') && (
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: theme.palette.text.secondary }}>Rol</InputLabel>
                    <Select
                      name="rol"
                      value={newUser.rol}
                      onChange={handleInputChange}
                      required
                      sx={{
                        color: theme.palette.text.primary,
                        '.MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.divider
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.light
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main
                        }
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: theme.palette.background.paper,
                            '& .MuiMenuItem-root': {
                              color: theme.palette.text.primary
                            },
                            '& .MuiMenuItem-root:hover': {
                              bgcolor: theme.palette.action.hover
                            }
                          }
                        }
                      }}
                    >
                      {user?.rol === 'partner'
                        ? [
                          <MenuItem key="user" value="user">Usuario</MenuItem>,
                          <MenuItem key="readonly" value="readonly">Solo lectura</MenuItem>
                        ]
                        : [
                          <MenuItem key="admin" value="admin">Admin</MenuItem>,
                          <MenuItem key="partner" value="partner">Partner</MenuItem>,
                          <MenuItem key="user" value="user">Usuario</MenuItem>,
                          <MenuItem key="readonly" value="readonly">Solo lectura</MenuItem>
                        ]
                      }
                    </Select>
                  </FormControl>
                )}
                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  <Button
                    onClick={handleCloseModal}
                    variant="contained"
                    sx={{
                      backgroundColor: theme.palette.action.hover,
                      color: theme.palette.text.secondary,
                      border: `1px solid ${theme.palette.divider}`,
                      '&:hover': {
                        backgroundColor: theme.palette.action.selected,
                        borderColor: theme.palette.text.secondary,
                      },
                      flex: 1,
                      textTransform: 'none',
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<CheckIcon sx={{ color: theme.palette.primary.contrastText }} />}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        borderColor: theme.palette.primary.dark,
                      },
                      flex: 1,
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      '& .MuiButton-startIcon': {
                        marginRight: 1,
                        '& svg': {
                          fontSize: '1.2rem'
                        }
                      }
                    }}
                  >
                    Confirmar
                  </Button>
                </Stack>
              </Stack>
            </form>
          </Paper>
        </Modal>

        {/* Modal de eliminación */}
        <Modal
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          aria-labelledby="delete-modal-title"
        >
          <Box sx={{
            ...modalStyle,
            width: 400,
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
          }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: theme.palette.text.primary, mb: 2, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
              Confirmar eliminación
            </Typography>
            <Typography sx={{ color: theme.palette.text.primary, mb: 3, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
              ¿Estás seguro de que deseas eliminar al usuario{' '}
              <span style={{ color: theme.palette.error.light }}>{userToDelete?.email}</span>?
              <br />
              Esta acción no se puede deshacer.
            </Typography>
            <Typography sx={{ color: theme.palette.text.secondary, mb: 2, fontSize: '0.9rem', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
              Mantén presionado el botón para eliminar
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                onClick={handleCloseDeleteModal}
                variant="outlined"
                disabled={isLoading}
                sx={{
                  color: theme.palette.text.primary,
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  borderColor: theme.palette.divider,
                  '&:hover': {
                    borderColor: theme.palette.primary.light,
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                Cancelar
              </Button>
              <Box sx={{ position: 'relative' }}>
                <Button
                  onMouseDown={startDeleteTimer}
                  onMouseUp={stopDeleteTimer}
                  onMouseLeave={stopDeleteTimer}
                  onTouchStart={startDeleteTimer}
                  onTouchEnd={stopDeleteTimer}
                  variant="contained"
                  disabled={isLoading}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: theme.palette.error.main,
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    color: theme.palette.getContrastText(theme.palette.error.main),
                    '&:hover': {
                      bgcolor: theme.palette.error.dark
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: `${deleteProgress}%`,
                      height: '100%',
                      backgroundColor: theme.palette.error.dark,
                      transition: 'width 0.01s linear'
                    }
                  }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                      Eliminando...
                    </>
                  ) : (
                    'Mantener para eliminar'
                  )}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Modal>

        {/* Modal de modificación */}
        <Modal
          open={openModifyModal}
          onClose={handleCloseModifyModal}
          aria-labelledby="modify-modal-title"
        >
          <Box sx={{
            ...modalStyle,
            width: 400,
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
          }}>
            <Typography id="modify-modal-title" variant="h6" sx={{ color: theme.palette.text.primary, mb: 2, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', textAlign: 'center' }}>
              Modificar Usuario
            </Typography>
            <Divider sx={{
              mb: 3,
              backgroundColor: theme.palette.divider,
              opacity: 0.5
            }} />
            <Stack spacing={3}>
              <TextField
                label="Nombre"
                value={modifiedUserData.name}
                onChange={handleModifyInputChange('name')}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: theme.palette.text.primary,
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.light,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    '&.Mui-focused': {
                      color: theme.palette.primary.main,
                    },
                  },
                }}
              />
              <TextField
                label="Email"
                value={modifiedUserData.email}
                onChange={handleModifyInputChange('email')}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: theme.palette.text.primary,
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.light,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    '&.Mui-focused': {
                      color: theme.palette.primary.main,
                    },
                  },
                }}
              />
              <FormControl fullWidth>
                <InputLabel sx={{
                  color: theme.palette.text.secondary,
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  '&.Mui-focused': {
                    color: theme.palette.primary.main,
                  }
                }}>
                  Organización
                </InputLabel>
                <Select
                  value={modifiedUserData.organization}
                  onChange={handleModifyInputChange('organization')}
                  label="Organización"
                  sx={{
                    color: theme.palette.text.primary,
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.light,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '& .MuiSelect-icon': {
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  {user?.organizations?.map((org) => (
                    <MenuItem
                      key={org.code}
                      value={org.code}
                      sx={{
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                      }}
                    >
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel sx={{
                  color: theme.palette.text.secondary,
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  '&.Mui-focused': {
                    color: theme.palette.primary.main,
                  }
                }}>
                  Rol
                </InputLabel>
                <Select
                  value={modifiedUserData.rol}
                  onChange={handleModifyInputChange('rol')}
                  label="Rol"
                  sx={{
                    color: theme.palette.text.primary,
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.light,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '& .MuiSelect-icon': {
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  <MenuItem value="user" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>Usuario</MenuItem>
                  <MenuItem value="admin" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>Admin</MenuItem>
                  <MenuItem value="partner" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>Partner</MenuItem>
                  <MenuItem value="readonly" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>Solo lectura</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
              <Button
                onClick={handleCloseModifyModal}
                variant="outlined"
                disabled={isLoading}
                sx={{
                  color: theme.palette.text.primary,
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  borderColor: theme.palette.divider,
                  '&:hover': {
                    borderColor: theme.palette.primary.light,
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmModify}
                variant="contained"
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark
                  }
                }}
              >
                {isLoading ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </Stack>
          </Box>
        </Modal>

        {/* Snackbar para notificaciones */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={snackbar.severity}
            sx={{
              backgroundColor: snackbar.severity === 'success' ? theme.palette.success.main : theme.palette.error.main,
              color: snackbar.severity === 'success'
                ? theme.palette.getContrastText(theme.palette.success.main)
                : theme.palette.getContrastText(theme.palette.error.main)
            }}
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default Usuarios;
